import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import { TimelineMax, Bounce, Power3 } from "gsap";
import { BLEND_MODES } from "pixi.js";
import { GameSetting } from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import { StageManager } from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import { Helper } from "@nolimitcity/slot-game/bin/core/utils/Helper";
import { GameConfig } from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";

import { PunkSymbol } from "../../../punkEngine/PunkSymbol";
import { PunkEngineManager } from "../../../punkEngine/PunkEngineManager";
import { PunkEngineController } from "../../../punkEngine/PunkEngineController";
import { ReelItemMap, IItemTransform, IReelItem } from "../../../punkEngine/mapping/ReelItemMap";
import { Sequence } from "@nolimitcity/slot-game/bin/core/displayobject/Sequence";

interface DividerItem extends IReelItem {
    seq:Sequence;
}

export class ReelSlicedDivider extends ReelItemMap {

    private static _instance:ReelSlicedDivider;
    protected _items:DividerItem[] = [];

    protected readonly _symbolArrSizes:number[] = [90,108,135,180,270,324,360];
    protected readonly animationSizeY:number = 90;

    constructor(){
        super();
        if(!ReelSlicedDivider._instance){
            ReelSlicedDivider._instance = this;

            StageManager.getLayer(GameConfig.instance.LAYERS.REEL_BACKGROUND.name).addChild(this);
        } 
    }

    public static get instance():ReelSlicedDivider{ return this._instance; }

    public updateExpandedOffset(reelID:number,symID:number,steps:number):void{
        let item:DividerItem = this.getItem(reelID,symID);
        item.SymID += steps;
        this.updateItemTransform(item,false);
    }

    public playDivider(reelID:number,symID:number,duration?:number):TimelineMax{
        const item:DividerItem = this.getItem(reelID, symID);
        this.updateItemTransform(item);
        const tl:TimelineMax = new TimelineMax({});
        if(!item.seq.visible){
            tl.add(() => item.seq.visible = true );
            tl.add(item.seq.playAnimation({duration:duration}))
            tl.play();
        } 
        return tl;
    }
   
    public changeSizeBg(reelID:number,duration:number = 0.2):TimelineMax{
        const tl:TimelineMax = new TimelineMax({});
        for(let i = 0; i < this._items.length;i++){
            if(this._items[i] && this._items[i].ReelID == reelID){
                let target:DividerItem = this._items[i];
                if(!PunkEngineManager.getReel(target.ReelID).symbolExcist(target.SymID)) continue;
                const symbol:PunkSymbol = PunkEngineManager.getReel(target.ReelID).getSymbol(target.SymID)!;
                const currHeight:number = this._symbolArrSizes[symbol.stateType.state];
                const nextHeight:number = this._symbolArrSizes[symbol.stateType.state-1];
                tl.to(target,duration,{ onUpdate:() => {
                    let off:number = (nextHeight-currHeight)*tl.progress();
                    target.seq.position = Helper.copyLocalPosition(target.seq, symbol);
                    target.seq.pivot.set(symbol.symbolWidht*0.5,-( ((currHeight+off)-this.animationSizeY)*0.5));
                },ease:Bounce.easeOut})
            }
        }
        tl.play();

        return tl;
    }

    public fadeOutAndRemove(reelID:number):void{
        const tl:TimelineMax = new TimelineMax({
            onComplete:() => this.cleanItems(reelID),
        });
        for(let i = 0; i < this._items.length;i++){
            if(this._items[i] && this._items[i].ReelID == reelID){
                let target:DividerItem = this._items[i];
                tl.to(target.seq,0.1 + (GameSetting.isFastSpin ? 0.1 : 0 ),{alpha:0, onUpdate: () => this.updateItemTransform(target, false), ease:Power3.easeIn},0)
            }
        }
        tl.play();
    }

    protected getItem(reelID:number,symID:number):DividerItem{
        if(this.getExistingItem(reelID, symID)){
            return this.getExistingItem(reelID, symID) as DividerItem;
        }

        const seq:Sequence = new Sequence({textures: GameResources.getTextures("RowDivider")});
        let item:DividerItem =  super.createItem(reelID, symID,{seq:seq}) as DividerItem;
        
        this.addChild(seq);
        seq.visible = false;
        seq.blendMode = BLEND_MODES.ADD;
        this._items.push(item);
        return item;
    }

    public addDividers(stops:boolean = false, reelID:number):void{
        const reelC:PunkEngineController = PunkEngineManager.getReel(reelID);
        for (let row: number = 0; row < reelC.AllWindowSymbols.length - 1; row++) {
            if(!reelC.Engine.reverse){
                if (stops){
                    if(reelC.getSymbol(row-1)!.isLowValue && reelC.getSymbol(row)!.isLowValue) 
                        this.playDivider(reelC.reelID, row, 0.2);
                }else{
                    let symPosID:number = reelC.AllWindowSymbols[row].posID;
                    if (reelC.getSymbol(symPosID)!.isLowValue && reelC.getSymbol(symPosID+1)!.isLowValue) {
                        this.playDivider(reelC.reelID, symPosID, 0.2);
                    }
                }
                continue;
            }
            const posFirst:number = stops ? reelC.AllWindowSymbols[row].posID+1 : reelC.AllWindowSymbols[row].posID;
            const posLast:number = posFirst+1;
            if(!reelC.symbolExcist(posFirst) || !reelC.symbolExcist(posLast)) continue;
            if (reelC.getSymbol(posFirst)!.isLowValue && reelC.getSymbol(posLast)!.isLowValue){
                this.playDivider(reelC.reelID, stops ? posFirst : posLast-1 , 0.2);
            }
        }
    }

    protected cleanItems(reelID:number):void{
        for(let i = 0; i < this._items.length;i++){
            if(this._items[i] && this._items[i].ReelID == reelID && PunkEngineManager.getReel(reelID).isActive)
                delete this._items[i];
        }
        this._items = this._items.filter((item:DividerItem) => {return !!item})
    }

    // Functions
    protected updateItemTransform(item:DividerItem,useMap:boolean = true):void{
        if(item == undefined) return;

        const transformData:IItemTransform = this.getSymbolTransformData(item, item.seq,useMap)!;
        item.seq.position = transformData.position;
        item.seq.scale = transformData.scale;

        item.seq.pivot.set(transformData.width*0.5,-((transformData.height-this.animationSizeY)*0.5));
    }
}